import { BaseGenerator } from './BaseGenerator.js';
import { ObjectPlacer } from './ObjectPlacer.js';
import { ZONE } from './ZoneTypes.js';
import { MapPipeline } from './MapPipeline.js';

/**
 * Village map generator.
 * Produces a village with buildings, roads, fences, gardens, and a town square.
 *
 * Supports three quality modes:
 * - "draft"    — direct tile ID output (legacy, fast)
 * - "standard" — pipeline with transition + cleanup passes
 * - "high"     — pipeline + full decoration (lampposts, trees, flowers)
 */
export class VillageGenerator extends BaseGenerator {
  generate(content) {
    const quality = this.options.quality || 'draft';

    if (quality === 'draft') {
      return this._generateDraft(content);
    }

    return this._generatePipeline(content, quality);
  }

  // ---- Pipeline mode (standard / high) ----

  _generatePipeline(content, quality) {
    const tiles = content.tiles || VillageGenerator.DEFAULT_TILES;
    const opts = { ...VillageGenerator.DEFAULT_OPTIONS, ...this.options };

    // 1. Structure pass — output zone map
    const ground = this.createGrid(this.width, this.height, ZONE.GRASS);
    const objects = this.createGrid(this.width, this.height, ZONE.EMPTY);
    const overhead = this.createGrid(this.width, this.height, ZONE.EMPTY);

    // Roads
    const roads = this._generateRoadsZone(ground, opts);

    // Town square
    const center = this._createTownSquareZone(ground, roads);

    // Building lots
    const lots = this._generateBuildingLotsZone(ground, opts);

    // Place buildings
    const buildings = [];
    for (const lot of lots) {
      const building = this._placeBuildingZone(ground, objects, overhead, lot, opts);
      if (building) buildings.push(building);
    }

    // Gardens
    this._addGardensZone(ground, objects, buildings, opts);

    // 2. Run pipeline
    const pipeline = MapPipeline.createDefault(this.rng, { tileMeta: this.options.tileMeta });

    if (quality !== 'high') {
      pipeline.removePass('decoration');
    }

    const pipelineOptions = {
      tileMapping: this._buildTileMapping(tiles),
      floorTileIds: [ZONE.GRASS, ZONE.ROAD, ZONE.STONE, ZONE.PATH],
      corridorTileId: ZONE.PATH,
    };

    if (quality === 'high') {
      pipelineOptions.decorations = this._buildDecorationSpecs(tiles, opts);
      pipelineOptions.edgeDecorations = [
        {
          edgeTileId: ZONE.ROAD,
          decorations: [
            { tileId: ZONE.LAMPPOST, chance: 0.08, spacing: 6 },
          ],
        },
        {
          edgeTileId: ZONE.GARDEN,
          decorations: [
            { tileId: ZONE.FLOWER, chance: 0.2, spacing: 2 },
          ],
        },
      ];
    }

    const result = pipeline.run({
      ground,
      objects,
      overhead,
      zones: ground,
      width: this.width,
      height: this.height,
      metadata: { buildings, roads, center },
    }, pipelineOptions);

    // 3. Resolve zones to tile IDs
    const finalGround = this._resolveZones(result.ground, tiles);
    const finalObjects = this._resolveObjectZones(result.objects, tiles);
    const finalOverhead = this._resolveObjectZones(result.overhead, tiles);

    // Street decor (well at center, operates on resolved IDs)
    if (center && tiles.well) {
      const cy = center.y;
      const cx = center.x;
      if (cy >= 0 && cy < this.height && cx >= 0 && cx < this.width) {
        finalObjects[cy][cx] = tiles.well;
      }
    }

    // Surrounding trees (edge of map)
    if (quality === 'high' && tiles.tree) {
      this._addSurroundingTreesZone(finalObjects, finalGround, finalOverhead, tiles);
    }

    // Redistribute tiles to correct layers based on tagger metadata
    if (tiles._tileLayers) {
      this.redistributeByLayer(
        { ground: finalGround, objects: finalObjects, overhead: finalOverhead },
        tiles._tileLayers,
      );
    }

    return this.buildTMJ({
      layers: [
        { name: 'Ground', data: this.flattenGrid(finalGround) },
        { name: 'Objects', data: this.flattenGrid(finalObjects) },
        { name: 'Overhead', data: this.flattenGrid(finalOverhead) },
      ],
      tilesets: content.tilesets || [],
      properties: {
        buildings: { type: 'string', value: JSON.stringify(buildings) },
      },
    });
  }

  _generateRoadsZone(ground, opts) {
    const roads = [];
    const roadW = opts.roadWidth || 3;

    // Main horizontal road
    const mainY = Math.floor(this.height * (0.4 + this.rng.nextFloat(-0.05, 0.05)));
    for (let x = 0; x < this.width; x++) {
      for (let d = 0; d < roadW; d++) {
        this.setCell(ground, x, mainY + d, ZONE.ROAD);
      }
    }
    roads.push({ x1: 0, y1: mainY, x2: this.width - 1, y2: mainY, dir: 'horizontal', width: roadW });

    // Main vertical road
    const mainX = Math.floor(this.width * (0.45 + this.rng.nextFloat(-0.05, 0.05)));
    for (let y = 0; y < this.height; y++) {
      for (let d = 0; d < roadW; d++) {
        this.setCell(ground, mainX + d, y, ZONE.ROAD);
      }
    }
    roads.push({ x1: mainX, y1: 0, x2: mainX, y2: this.height - 1, dir: 'vertical', width: roadW });

    // Side roads
    const sideCount = opts.sideRoads || this.rng.nextInt(2, 4);
    for (let i = 0; i < sideCount; i++) {
      if (this.rng.chance(0.5)) {
        const y = this.rng.nextInt(4, this.height - 5);
        if (Math.abs(y - mainY) < 6) continue;
        for (let x = 0; x < this.width; x++) {
          for (let d = 0; d < 2; d++) {
            this.setCell(ground, x, y + d, ZONE.ROAD);
          }
        }
        roads.push({ x1: 0, y1: y, x2: this.width - 1, y2: y, dir: 'horizontal', width: 2 });
      } else {
        const x = this.rng.nextInt(4, this.width - 5);
        if (Math.abs(x - mainX) < 6) continue;
        for (let y = 0; y < this.height; y++) {
          for (let d = 0; d < 2; d++) {
            this.setCell(ground, x + d, y, ZONE.ROAD);
          }
        }
        roads.push({ x1: x, y1: 0, x2: x, y2: this.height - 1, dir: 'vertical', width: 2 });
      }
    }

    return roads;
  }

  _createTownSquareZone(ground, roads) {
    const hRoad = roads.find(r => r.dir === 'horizontal');
    const vRoad = roads.find(r => r.dir === 'vertical');
    if (!hRoad || !vRoad) return null;

    const cx = vRoad.x1 + Math.floor(vRoad.width / 2);
    const cy = hRoad.y1 + Math.floor(hRoad.width / 2);
    const size = 4;

    for (let dy = -size; dy <= size; dy++) {
      for (let dx = -size; dx <= size; dx++) {
        this.setCell(ground, cx + dx, cy + dy, ZONE.STONE);
      }
    }

    return { x: cx, y: cy, size };
  }

  _generateBuildingLotsZone(ground, opts) {
    const lots = [];
    const maxBuildings = opts.maxBuildings || 12;
    const lotSize = opts.buildingSize || { min: 4, max: 7 };

    for (let i = 0; i < maxBuildings * 3 && lots.length < maxBuildings; i++) {
      const w = this.rng.nextInt(lotSize.min, lotSize.max);
      const h = this.rng.nextInt(lotSize.min, lotSize.max);
      const x = this.rng.nextInt(2, this.width - w - 2);
      const y = this.rng.nextInt(2, this.height - h - 2);

      let clear = true;
      for (let ly = y - 1; ly <= y + h; ly++) {
        for (let lx = x - 1; lx <= x + w; lx++) {
          const cell = this.getCell(ground, lx, ly);
          if (cell !== ZONE.GRASS) { clear = false; break; }
        }
        if (!clear) break;
      }

      if (clear) {
        let overlaps = false;
        for (const lot of lots) {
          if (x < lot.x + lot.width + 2 && x + w + 2 > lot.x &&
              y < lot.y + lot.height + 2 && y + h + 2 > lot.y) {
            overlaps = true;
            break;
          }
        }
        if (!overlaps) {
          // Prefer road-adjacent positions; skip non-adjacent until 30 attempts exhausted
          let nearRoad = false;
          outer: for (let by = y - 2; by < y + h + 2; by++) {
            for (let bx = x - 2; bx < x + w + 2; bx++) {
              const cell = this.getCell(ground, bx, by);
              if (cell === ZONE.ROAD || cell === ZONE.STONE || cell === ZONE.PATH) {
                nearRoad = true;
                break outer;
              }
            }
          }
          if (!nearRoad && i < 30) continue;
          lots.push({ x, y, width: w, height: h });
        }
      }
    }

    return lots;
  }

  _placeBuildingZone(ground, objects, overhead, lot, opts) {
    const { x, y, width: w, height: h } = lot;

    // Floor
    for (let by = y; by < y + h; by++) {
      for (let bx = x; bx < x + w; bx++) {
        this.setCell(ground, bx, by, ZONE.FLOOR);
      }
    }

    // Walls (on objects layer)
    for (let bx = x; bx < x + w; bx++) {
      this.setCell(objects, bx, y, ZONE.WALL_H);
      this.setCell(objects, bx, y + h - 1, ZONE.WALL_H);
    }
    for (let by = y; by < y + h; by++) {
      this.setCell(objects, x, by, ZONE.WALL_V);
      this.setCell(objects, x + w - 1, by, ZONE.WALL_V);
    }

    // Corners
    this.setCell(objects, x, y, ZONE.WALL_CORNER);
    this.setCell(objects, x + w - 1, y, ZONE.WALL_CORNER);
    this.setCell(objects, x, y + h - 1, ZONE.WALL_CORNER);
    this.setCell(objects, x + w - 1, y + h - 1, ZONE.WALL_CORNER);

    // Door
    const doorX = x + Math.floor(w / 2);
    this.setCell(objects, doorX, y + h - 1, ZONE.DOOR);

    // Roof on overhead (edges get ROOF_EDGE for visual variation)
    for (let by = y; by < y + h; by++) {
      for (let bx = x; bx < x + w; bx++) {
        const isEdge = bx === x || bx === x + w - 1 || by === y || by === y + h - 1;
        this.setCell(overhead, bx, by, isEdge ? ZONE.ROOF_EDGE : ZONE.ROOF);
      }
    }

    return { x, y, width: w, height: h, doorX, doorY: y + h - 1 };
  }

  _addGardensZone(ground, objects, buildings, opts) {
    for (const building of buildings) {
      if (!this.rng.chance(0.4)) continue;

      const side = this.rng.nextInt(0, 3);
      let gx, gy, gw, gh;

      switch (side) {
        case 0: gx = building.x; gy = building.y - 3; gw = building.width; gh = 2; break;
        case 1: gx = building.x; gy = building.y + building.height + 1; gw = building.width; gh = 2; break;
        case 2: gx = building.x - 3; gy = building.y; gw = 2; gh = building.height; break;
        case 3: gx = building.x + building.width + 1; gy = building.y; gw = 2; gh = building.height; break;
      }

      for (let dy = 0; dy < gh; dy++) {
        for (let dx = 0; dx < gw; dx++) {
          const cell = this.getCell(ground, gx + dx, gy + dy);
          if (cell === ZONE.GRASS) {
            this.setCell(ground, gx + dx, gy + dy, ZONE.GARDEN);
            if (this.rng.chance(0.3)) {
              this.setCell(objects, gx + dx, gy + dy, ZONE.CROPS);
            }
          }
        }
      }

      // Fence around garden
      for (let dx = -1; dx <= gw; dx++) {
        if (this.getCell(objects, gx + dx, gy - 1) === ZONE.EMPTY) {
          this.setCell(objects, gx + dx, gy - 1, ZONE.FENCE);
        }
        if (this.getCell(objects, gx + dx, gy + gh) === ZONE.EMPTY) {
          this.setCell(objects, gx + dx, gy + gh, ZONE.FENCE);
        }
      }
      for (let dy = -1; dy <= gh; dy++) {
        if (this.getCell(objects, gx - 1, gy + dy) === ZONE.EMPTY) {
          this.setCell(objects, gx - 1, gy + dy, ZONE.FENCE);
        }
        if (this.getCell(objects, gx + gw, gy + dy) === ZONE.EMPTY) {
          this.setCell(objects, gx + gw, gy + dy, ZONE.FENCE);
        }
      }
    }
  }

  _addSurroundingTreesZone(objects, ground, overhead, tiles) {
    const edgeMargin = 3;
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (x > edgeMargin && x < this.width - edgeMargin &&
            y > edgeMargin && y < this.height - edgeMargin) continue;
        if (ground[y][x] !== tiles.grass) continue;
        if (objects[y][x] !== 0) continue;
        if (this.rng.chance(0.25)) {
          objects[y][x] = tiles.tree;
          if (tiles.treeTop && y > 0) {
            overhead[y - 1][x] = tiles.treeTop;
          }
        }
      }
    }
  }

  _buildTileMapping(tiles) {
    return {
      [ZONE.GRASS]:  { center: tiles.grass },
      [ZONE.ROAD]:   { center: tiles.road },
      [ZONE.STONE]:  { center: tiles.cobble || tiles.road },
      [ZONE.FLOOR]:  { center: tiles.floor || tiles.cobble || tiles.road },
      [ZONE.GARDEN]: { center: tiles.garden || tiles.grass },
      [ZONE.PATH]:   { center: tiles.road },
    };
  }

  _buildDecorationSpecs(tiles, opts) {
    const specs = [];
    if (tiles.lamppost) {
      specs.push({
        tileId: ZONE.LAMPPOST,
        density: 0.005,
        spacing: 6,
        validGround: [ZONE.ROAD],
        avoidEdges: false,
      });
    }
    return specs;
  }

  _resolveZones(grid, tiles) {
    const mapping = {
      [ZONE.GRASS]:  tiles.grass,
      [ZONE.ROAD]:   tiles.road,
      [ZONE.STONE]:  tiles.cobble || tiles.road,
      [ZONE.FLOOR]:  tiles.floor || tiles.cobble || tiles.road,
      [ZONE.GARDEN]: tiles.garden || tiles.grass,
      [ZONE.PATH]:   tiles.road,
    };
    return grid.map(row => row.map(z => mapping[z] !== undefined ? mapping[z] : z));
  }

  _resolveObjectZones(grid, tiles) {
    return grid.map(row => row.map(z => {
      if (z === ZONE.EMPTY || z === 0) return 0;
      if (z === ZONE.WALL_H) return tiles.wallH;
      if (z === ZONE.WALL_V) return tiles.wallV;
      if (z === ZONE.WALL_CORNER) return tiles.wallCorner || tiles.wallH;
      if (z === ZONE.DOOR) return tiles.door || 0;
      if (z === ZONE.ROOF) return tiles.roof || 0;
      if (z === ZONE.ROOF_EDGE) return tiles.roofEdge || tiles.roof || 0;
      if (z === ZONE.FENCE) return tiles.fence || 0;
      if (z === ZONE.CROPS) {
        const cropTiles = Array.isArray(tiles.crops) ? tiles.crops : (tiles.crops ? [tiles.crops] : [0]);
        return this.rng.pick(cropTiles);
      }
      if (z === ZONE.LAMPPOST) return tiles.lamppost || 0;
      if (z === ZONE.WELL) return tiles.well || 0;
      if (z === ZONE.TREE) return tiles.tree || 0;
      if (z === ZONE.FLOWER) return tiles.decorations?.[0] || 0;
      return z;
    }));
  }

  // ---- Draft mode (original direct tile output) ----

  _generateDraft(content) {
    const tiles = content.tiles || VillageGenerator.DEFAULT_TILES;
    const opts = { ...VillageGenerator.DEFAULT_OPTIONS, ...this.options };

    const ground = this.createGrid(this.width, this.height, tiles.grass);
    const objects = this.createGrid(this.width, this.height, 0);
    const overhead = this.createGrid(this.width, this.height, 0);

    // 1. Roads
    const roads = this._generateRoads(ground, tiles, opts);

    // 2. Town square
    const center = this._createTownSquare(ground, tiles, roads);

    // 3. Building lots
    const lots = this._generateBuildingLots(ground, tiles, roads, opts);

    // 4. Place buildings
    const buildings = [];
    for (const lot of lots) {
      const building = this._placeBuilding(ground, objects, overhead, lot, tiles, opts);
      if (building) buildings.push(building);
    }

    // 5. Gardens and fences
    this._addGardens(ground, objects, buildings, tiles, opts);

    // 6. Street decoration
    this._addStreetDecor(objects, ground, tiles, roads, center);

    // 7. Surrounding terrain
    this._addSurroundingTerrain(ground, objects, overhead, tiles, opts);

    if (tiles._tileLayers) {
      this.redistributeByLayer({ ground, objects, overhead }, tiles._tileLayers);
    }

    return this.buildTMJ({
      layers: [
        { name: 'Ground', data: this.flattenGrid(ground) },
        { name: 'Objects', data: this.flattenGrid(objects) },
        { name: 'Overhead', data: this.flattenGrid(overhead) },
      ],
      tilesets: content.tilesets || [],
      properties: {
        buildings: { type: 'string', value: JSON.stringify(buildings) },
      },
    });
  }

  // ---- Original helpers (used by draft mode) ----

  _generateRoads(ground, tiles, opts) {
    const roads = [];

    const mainY = Math.floor(this.height * (0.4 + this.rng.nextFloat(-0.05, 0.05)));
    const roadW = opts.roadWidth || 3;
    for (let x = 0; x < this.width; x++) {
      for (let d = 0; d < roadW; d++) {
        this.setCell(ground, x, mainY + d, tiles.road);
      }
    }
    roads.push({ x1: 0, y1: mainY, x2: this.width - 1, y2: mainY, dir: 'horizontal', width: roadW });

    const mainX = Math.floor(this.width * (0.45 + this.rng.nextFloat(-0.05, 0.05)));
    for (let y = 0; y < this.height; y++) {
      for (let d = 0; d < roadW; d++) {
        this.setCell(ground, mainX + d, y, tiles.road);
      }
    }
    roads.push({ x1: mainX, y1: 0, x2: mainX, y2: this.height - 1, dir: 'vertical', width: roadW });

    const sideCount = opts.sideRoads || this.rng.nextInt(2, 4);
    for (let i = 0; i < sideCount; i++) {
      if (this.rng.chance(0.5)) {
        const y = this.rng.nextInt(4, this.height - 5);
        if (Math.abs(y - mainY) < 6) continue;
        for (let x = 0; x < this.width; x++) {
          for (let d = 0; d < 2; d++) {
            this.setCell(ground, x, y + d, tiles.road);
          }
        }
        roads.push({ x1: 0, y1: y, x2: this.width - 1, y2: y, dir: 'horizontal', width: 2 });
      } else {
        const x = this.rng.nextInt(4, this.width - 5);
        if (Math.abs(x - mainX) < 6) continue;
        for (let y = 0; y < this.height; y++) {
          for (let d = 0; d < 2; d++) {
            this.setCell(ground, x + d, y, tiles.road);
          }
        }
        roads.push({ x1: x, y1: 0, x2: x, y2: this.height - 1, dir: 'vertical', width: 2 });
      }
    }

    return roads;
  }

  _createTownSquare(ground, tiles, roads) {
    const hRoad = roads.find(r => r.dir === 'horizontal');
    const vRoad = roads.find(r => r.dir === 'vertical');
    if (!hRoad || !vRoad) return null;

    const cx = vRoad.x1 + Math.floor(vRoad.width / 2);
    const cy = hRoad.y1 + Math.floor(hRoad.width / 2);
    const size = 4;

    for (let dy = -size; dy <= size; dy++) {
      for (let dx = -size; dx <= size; dx++) {
        this.setCell(ground, cx + dx, cy + dy, tiles.cobble || tiles.road);
      }
    }

    return { x: cx, y: cy, size };
  }

  _generateBuildingLots(ground, tiles, roads, opts) {
    const lots = [];
    const maxBuildings = opts.maxBuildings || 12;
    const lotSize = opts.buildingSize || { min: 4, max: 7 };

    for (let i = 0; i < maxBuildings * 3 && lots.length < maxBuildings; i++) {
      const w = this.rng.nextInt(lotSize.min, lotSize.max);
      const h = this.rng.nextInt(lotSize.min, lotSize.max);
      const x = this.rng.nextInt(2, this.width - w - 2);
      const y = this.rng.nextInt(2, this.height - h - 2);

      let clear = true;
      for (let ly = y - 1; ly <= y + h; ly++) {
        for (let lx = x - 1; lx <= x + w; lx++) {
          const cell = this.getCell(ground, lx, ly);
          if (cell !== tiles.grass) { clear = false; break; }
        }
        if (!clear) break;
      }

      if (clear) {
        let overlaps = false;
        for (const lot of lots) {
          if (x < lot.x + lot.width + 2 && x + w + 2 > lot.x &&
              y < lot.y + lot.height + 2 && y + h + 2 > lot.y) {
            overlaps = true;
            break;
          }
        }
        if (!overlaps) {
          // Prefer road-adjacent positions; skip non-adjacent until 30 attempts exhausted
          let nearRoad = false;
          outer: for (let by = y - 2; by < y + h + 2; by++) {
            for (let bx = x - 2; bx < x + w + 2; bx++) {
              const cell = this.getCell(ground, bx, by);
              if (cell === tiles.road || cell === (tiles.cobble || tiles.road)) {
                nearRoad = true;
                break outer;
              }
            }
          }
          if (!nearRoad && i < 30) continue;
          lots.push({ x, y, width: w, height: h });
        }
      }
    }

    return lots;
  }

  _placeBuilding(ground, objects, overhead, lot, tiles, opts) {
    const { x, y, width: w, height: h } = lot;

    for (let by = y; by < y + h; by++) {
      for (let bx = x; bx < x + w; bx++) {
        this.setCell(ground, bx, by, tiles.floor || tiles.cobble || tiles.road);
      }
    }

    for (let bx = x; bx < x + w; bx++) {
      this.setCell(objects, bx, y, tiles.wallH);
      this.setCell(objects, bx, y + h - 1, tiles.wallH);
    }
    for (let by = y; by < y + h; by++) {
      this.setCell(objects, x, by, tiles.wallV);
      this.setCell(objects, x + w - 1, by, tiles.wallV);
    }

    if (tiles.wallCorner) {
      this.setCell(objects, x, y, tiles.wallCorner);
      this.setCell(objects, x + w - 1, y, tiles.wallCorner);
      this.setCell(objects, x, y + h - 1, tiles.wallCorner);
      this.setCell(objects, x + w - 1, y + h - 1, tiles.wallCorner);
    }

    const doorX = x + Math.floor(w / 2);
    this.setCell(objects, doorX, y + h - 1, tiles.door || 0);

    if (tiles.roof) {
      for (let by = y; by < y + h; by++) {
        for (let bx = x; bx < x + w; bx++) {
          this.setCell(overhead, bx, by, tiles.roof);
        }
      }
    }

    return { x, y, width: w, height: h, doorX, doorY: y + h - 1 };
  }

  _addGardens(ground, objects, buildings, tiles, opts) {
    if (!tiles.garden) return;

    for (const building of buildings) {
      if (!this.rng.chance(0.4)) continue;

      const side = this.rng.nextInt(0, 3);
      let gx, gy, gw, gh;

      switch (side) {
        case 0: gx = building.x; gy = building.y - 3; gw = building.width; gh = 2; break;
        case 1: gx = building.x; gy = building.y + building.height + 1; gw = building.width; gh = 2; break;
        case 2: gx = building.x - 3; gy = building.y; gw = 2; gh = building.height; break;
        case 3: gx = building.x + building.width + 1; gy = building.y; gw = 2; gh = building.height; break;
      }

      for (let dy = 0; dy < gh; dy++) {
        for (let dx = 0; dx < gw; dx++) {
          const cell = this.getCell(ground, gx + dx, gy + dy);
          if (cell === tiles.grass) {
            this.setCell(ground, gx + dx, gy + dy, tiles.garden);
            if (this.rng.chance(0.3) && tiles.crops) {
              this.setCell(objects, gx + dx, gy + dy, this.rng.pick(
                Array.isArray(tiles.crops) ? tiles.crops : [tiles.crops]
              ));
            }
          }
        }
      }

      if (tiles.fence) {
        for (let dx = -1; dx <= gw; dx++) {
          this.setCell(objects, gx + dx, gy - 1, tiles.fence);
          this.setCell(objects, gx + dx, gy + gh, tiles.fence);
        }
        for (let dy = -1; dy <= gh; dy++) {
          this.setCell(objects, gx - 1, gy + dy, tiles.fence);
          this.setCell(objects, gx + gw, gy + dy, tiles.fence);
        }
      }
    }
  }

  _addStreetDecor(objects, ground, tiles, roads, center) {
    if (tiles.lamppost) {
      for (const road of roads) {
        if (road.dir === 'horizontal') {
          for (let x = road.x1 + 3; x < road.x2; x += this.rng.nextInt(6, 10)) {
            this.setCell(objects, x, road.y1 - 1, tiles.lamppost);
          }
        } else {
          for (let y = road.y1 + 3; y < road.y2; y += this.rng.nextInt(6, 10)) {
            this.setCell(objects, road.x1 - 1, y, tiles.lamppost);
          }
        }
      }
    }

    if (center && tiles.well) {
      this.setCell(objects, center.x, center.y, tiles.well);
    }
  }

  _addSurroundingTerrain(ground, objects, overhead, tiles, opts) {
    if (!tiles.tree) return;
    const edgeMargin = 3;

    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (x > edgeMargin && x < this.width - edgeMargin &&
            y > edgeMargin && y < this.height - edgeMargin) continue;
        if (ground[y][x] !== tiles.grass) continue;
        if (this.rng.chance(0.25)) {
          this.setCell(objects, x, y, tiles.tree);
          if (tiles.treeTop && y > 0) {
            this.setCell(overhead, x, y - 1, tiles.treeTop);
          }
        }
      }
    }
  }

  /**
   * Analyze tileset image visibility using Canvas API.
   * @param {HTMLImageElement} img
   * @param {number} tileW
   * @param {number} tileH
   * @returns {Map<number, boolean>}
   */
  static analyzeVisibleTiles(img, tileW, tileH) {
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    const imageData = ctx.getImageData(0, 0, img.width, img.height);

    const cols = Math.floor(img.width / tileW);
    const rows = Math.floor(img.height / tileH);
    const visibility = new Map();

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const tileId = row * cols + col;
        let hasVisiblePixel = false;

        for (let py = 0; py < tileH && !hasVisiblePixel; py++) {
          for (let px = 0; px < tileW && !hasVisiblePixel; px++) {
            const imgX = col * tileW + px;
            const imgY = row * tileH + py;
            const idx = (imgY * img.width + imgX) * 4;
            if (imageData.data[idx + 3] > 0) {
              hasVisiblePixel = true;
            }
          }
        }

        visibility.set(tileId, hasVisiblePixel);
      }
    }

    return visibility;
  }

  static DEFAULT_TILES = {
    grass: 1,
    road: 5,
    cobble: 6,
    floor: 7,
    wallH: 10,
    wallV: 11,
    wallCorner: 12,
    door: 13,
    roof: 14,
    garden: 15,
    crops: [16, 17],
    fence: 18,
    tree: 30,
    treeTop: 31,
    lamppost: 40,
    well: 41,
  };

  static DEFAULT_OPTIONS = {
    roadWidth: 3,
    sideRoads: 3,
    maxBuildings: 10,
    buildingSize: { min: 4, max: 7 },
  };

  static getEmittedZones() {
    return [
      ZONE.GRASS, ZONE.ROAD, ZONE.PATH, ZONE.STONE, ZONE.FLOOR, ZONE.WALL_H, ZONE.WALL_V, ZONE.WALL_CORNER, ZONE.DOOR, ZONE.FENCE, ZONE.ROOF, ZONE.ROOF_EDGE, ZONE.TREE, ZONE.FLOWER, ZONE.CROPS, ZONE.GARDEN, ZONE.WELL, ZONE.LAMPPOST,
    ];
  }
}
